// FuelForm component for entering fuel record data - Modern Design

import { useState, useEffect } from "react";
import { getTodayDate } from "../../utils/formatters";
import SearchableSelect from "../common/SearchableSelect";
import { getVehicles } from "../../services/api";

const FuelForm = ({ onSubmit, editingRecord, onCancelEdit }) => {
  const [formData, setFormData] = useState({
    license_plate: "",
    date: getTodayDate(),
    amount: "",
    oil_price: "",
    oil_volume: "",
    gas_station: "",
    mileage: "",
  });

  const [hasMileage, setHasMileage] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);

  // Fetch vehicles from API
  useEffect(() => {
    const fetchVehicles = async () => {
      setLoadingVehicles(true);
      try {
        const result = await getVehicles();
        if (result.success && result.data) {
          setVehicles(result.data);
        }
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      } finally {
        setLoadingVehicles(false);
      }
    };
    fetchVehicles();
  }, []);

  // Load editing record data
  useEffect(() => {
    if (editingRecord) {
      const mileageVal = editingRecord.mileage;
      const hasMil = mileageVal !== null && mileageVal !== undefined && parseFloat(mileageVal) > 0;
      setHasMileage(hasMil);
      setFormData({
        license_plate: editingRecord.license_plate || "",
        date: editingRecord.date || getTodayDate(),
        amount: editingRecord.amount?.toString() || "",
        oil_price: editingRecord.oil_price?.toString() || "",
        oil_volume: editingRecord.oil_volume?.toString() || "",
        gas_station: editingRecord.gas_station || "",
        mileage: hasMil ? mileageVal.toString() : "",
      });
    }
  }, [editingRecord]);

  // Prevent scroll wheel from changing number input values
  const handleWheel = (e) => {
    e.target.blur();
  };

  const handleInputChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Clamp oil_price in real-time: max 60
    if (name === "oil_price" && value !== "") {
      const num = parseFloat(value);
      if (!isNaN(num) && num > 60) value = "60";
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Auto-calculate oil_volume when amount and oil_price are provided
    if (name === "amount" || name === "oil_price") {
      const amount =
        name === "amount" ? parseFloat(value) : parseFloat(formData.amount);
      const price =
        name === "oil_price"
          ? parseFloat(value)
          : parseFloat(formData.oil_price);

      if (amount && price && price > 0) {
        const rawVolume = amount / price;
        const volume = Math.min(rawVolume, 100).toFixed(3);
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          oil_volume: volume,
        }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const data = {
      license_plate: formData.license_plate,
      date: formData.date,
      amount: parseFloat(formData.amount) || 0,
      oil_price: parseFloat(formData.oil_price) || 0,
      oil_volume: parseFloat(formData.oil_volume) || 0,
      gas_station: formData.gas_station,
      mileage: hasMileage ? (parseFloat(formData.mileage) || 0) : null,
    };

    onSubmit(data);
    resetForm();
  };

  const resetForm = () => {
    setHasMileage(true);
    setFormData({
      license_plate: "",
      date: getTodayDate(),
      amount: "",
      oil_price: "",
      oil_volume: "",
      gas_station: "",
      mileage: "",
    });
  };

  const handleCancel = () => {
    resetForm();
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  return (
    <div className="bg-gradient-to-br from-white to-orange-50 rounded-2xl shadow-2xl p-8 mb-8 border border-orange-100">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform duration-300">
              <svg
                className="w-8 h-8 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-4">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                {editingRecord
                  ? "✏️ แก้ไขข้อมูลการเติมน้ำมัน"
                  : "⛽ เพิ่มข้อมูลการเติมน้ำมัน"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {editingRecord
                  ? "กรุณาแก้ไขข้อมูลที่ต้องการเปลี่ยนแปลง"
                  : "บันทึกข้อมูลการเติมน้ำมันรถอย่างละเอียด"}
              </p>
            </div>
          </div>
          {editingRecord && (
            <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-lg border border-amber-300">
              <svg
                className="w-5 h-5 text-amber-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm font-medium text-amber-700">
                โหมดแก้ไข
              </span>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section 1: ข้อมูลรถและวันที่ */}
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 ml-3">
              🚗 ข้อมูลรถและวันที่
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* ป้ายทะเบียน */}
            <div>
              <SearchableSelect
                name="license_plate"
                value={formData.license_plate}
                onChange={handleInputChange}
                options={vehicles}
                placeholder={loadingVehicles ? "กำลังโหลด..." : "เลือกป้ายทะเบียน..."}
                label="ป้ายทะเบียน *"
                disabled={loadingVehicles}
                icon={
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                }
              />
            </div>

            {/* วันที่ */}
            <div className="group">
              <label
                htmlFor="date"
                className="flex items-center text-sm font-bold text-gray-700 mb-2"
              >
                <span className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center mr-2">
                  📆
                </span>
                วันที่
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="date"
                  name="date"
                  required
                  max={getTodayDate()}
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-green-300 focus:border-green-500 transition-all duration-300 text-gray-700 font-medium hover:border-green-400 group-hover:shadow-lg"
                />
              </div>
            </div>

            {/* เลขไมล์ */}
            <div className="group">
              <div className="flex items-center justify-between mb-2">
                <label
                  htmlFor="mileage"
                  className="flex items-center text-sm font-bold text-gray-700"
                >
                  <span className="w-7 h-7 bg-purple-100 rounded-lg flex items-center justify-center mr-2">
                    📊
                  </span>
                  เลขไมล์
                  {hasMileage && <span className="text-red-500 ml-1">*</span>}
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hasMileage}
                    onChange={(e) => {
                      setHasMileage(e.target.checked);
                      if (!e.target.checked) {
                        setFormData((prev) => ({ ...prev, mileage: "" }));
                      }
                    }}
                    className="w-4 h-4 accent-purple-500 cursor-pointer"
                  />
                  <span className="text-xs text-gray-500">มีเลขไมล์</span>
                </label>
              </div>
              <div className="relative">
                <input
                  type="number"
                  id="mileage"
                  name="mileage"
                  required={hasMileage}
                  disabled={!hasMileage}
                  min="0"
                  step="0.1"
                  placeholder={hasMileage ? "0.0" : "ไม่มีเลขไมล์"}
                  value={formData.mileage}
                  onChange={handleInputChange}
                  onWheel={handleWheel}
                  className={`w-full px-4 py-3.5 border-2 rounded-xl transition-all duration-300 font-medium ${
                    hasMileage
                      ? "border-gray-300 text-gray-700 hover:border-purple-400 focus:ring-4 focus:ring-purple-300 focus:border-purple-500 group-hover:shadow-lg"
                      : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <span className={`text-sm font-medium ${hasMileage ? "text-gray-400" : "text-gray-300"}`}>km</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: ข้อมูลปั๊มน้ำมัน */}
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center shadow-md">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 ml-3">
              ⛽ ข้อมูลปั๊มน้ำมัน
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-6">
            {/* ปั๊มน้ำมัน */}
            <div className="group">
              <label className="flex items-center text-sm font-bold text-gray-700 mb-3">
                <span className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center mr-2">
                  🏪
                </span>
                ปั๊มน้ำมัน
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="flex gap-4">
                {[
                  { value: "บางจาก", label: "บางจาก", color: "emerald" },
                  { value: "ปตท", label: "ปตท", color: "red" },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl border-2 cursor-pointer transition-all duration-200 font-medium ${
                      formData.gas_station === opt.value
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700 shadow-md"
                        : "border-gray-200 bg-white text-gray-600 hover:border-emerald-300 hover:bg-emerald-50/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="gas_station"
                      value={opt.value}
                      checked={formData.gas_station === opt.value}
                      onChange={handleInputChange}
                      required
                      className="accent-emerald-500"
                    />
                    <span className="text-base">⛽ {opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: ข้อมูลการเติม */}
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg flex items-center justify-center shadow-md">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                <path
                  fillRule="evenodd"
                  d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 ml-3">
              💰 ข้อมูลการเติม
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* จำนวนเงิน */}
            <div className="group">
              <label
                htmlFor="amount"
                className="flex items-center text-sm font-bold text-gray-700 mb-2"
              >
                <span className="w-7 h-7 bg-yellow-100 rounded-lg flex items-center justify-center mr-2">
                  💵
                </span>
                จำนวนเงิน
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                  <span className="text-gray-500 text-lg font-medium">฿</span>
                </div>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  required
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={handleInputChange}
                  onWheel={handleWheel}
                  className="w-full pl-10 pr-16 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-yellow-300 focus:border-yellow-500 transition-all duration-300 text-gray-700 font-medium hover:border-yellow-400 group-hover:shadow-lg"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <span className="text-gray-400 text-sm font-medium">บาท</span>
                </div>
              </div>
            </div>

            {/* ราคาน้ำมันต่อลิตร */}
            <div className="group">
              <label
                htmlFor="oil_price"
                className="flex items-center text-sm font-bold text-gray-700 mb-2"
              >
                <span className="w-7 h-7 bg-pink-100 rounded-lg flex items-center justify-center mr-2">
                  💲
                </span>
                ราคา/ลิตร
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                  <span className="text-gray-500 text-lg font-medium">฿</span>
                </div>
                <input
                  type="number"
                  id="oil_price"
                  name="oil_price"
                  required
                  min="20"
                  max="60"
                  step="0.01"
                  placeholder="20-60"
                  value={formData.oil_price}
                  onChange={handleInputChange}
                  onWheel={handleWheel}
                  className="w-full pl-10 pr-16 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-pink-300 focus:border-pink-500 transition-all duration-300 text-gray-700 font-medium hover:border-pink-400 group-hover:shadow-lg"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <span className="text-gray-400 text-sm font-medium">บาท</span>
                </div>
              </div>
              <p className="mt-1.5 text-xs text-pink-600 font-medium">ช่วงที่กำหนด: 20 - 60 บาท/ลิตร</p>
            </div>

            {/* ปริมาณน้ำมัน */}
            <div className="group">
              <label
                htmlFor="oil_volume"
                className="flex items-center text-sm font-bold text-gray-700 mb-2"
              >
                <span className="w-7 h-7 bg-cyan-100 rounded-lg flex items-center justify-center mr-2">
                  🛢️
                </span>
                ปริมาณน้ำมัน
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="oil_volume"
                  name="oil_volume"
                  required
                  min="0"
                  max="100"
                  step="0.001"
                  placeholder="0.000"
                  value={formData.oil_volume}
                  onChange={handleInputChange}
                  onWheel={handleWheel}
                  className="w-full px-4 pr-16 py-3.5 border-2 border-cyan-300 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl focus:ring-4 focus:ring-cyan-300 focus:border-cyan-500 transition-all duration-300 text-gray-700 font-bold hover:border-cyan-400 group-hover:shadow-lg cursor-not-allowed"
                  readOnly
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <span className="text-cyan-600 text-sm font-medium">
                    ลิตร
                  </span>
                </div>
              </div>
              <div className="flex items-center mt-2 text-xs text-cyan-700 bg-cyan-50 px-3 py-2 rounded-lg border border-cyan-200">
                <svg
                  className="w-4 h-4 mr-1.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">
                  คำนวณอัตโนมัติ (สูงสุด 100 ลิตร)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-4">
          {editingRecord && (
            <button
              type="button"
              onClick={handleCancel}
              className="group px-8 py-4 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-bold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg
                className="w-5 h-5 group-hover:rotate-12 transition-transform"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              ยกเลิก
            </button>
          )}
          <button
            type="submit"
            className="group px-10 py-4 bg-gradient-to-r from-orange-500 via-orange-600 to-red-600 hover:from-orange-600 hover:via-orange-700 hover:to-red-700 text-white font-bold rounded-xl transition-all duration-300 flex items-center gap-3 shadow-xl hover:shadow-2xl transform hover:scale-105"
          >
            <svg
              className="w-6 h-6 group-hover:scale-110 transition-transform"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-lg">
              {editingRecord ? "💾 อัปเดตข้อมูล" : "✅ บันทึกข้อมูล"}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default FuelForm;
